// 쿠폰함(§15) 점주검증 — QR 스캔(AppQrEntry)이 여기로 온다.
// [2026-11-04] 기프트 검증(VerifyConfirm)과 같은 흐름으로 바꿨다:
//  /mobile/couponverifyconfirm/{storeNo}/{promotionCouponNo}
//   storeNo = 상점 발행 쿠폰이면 발행 상점, 운영자·채널 쿠폰이면 로그인 상점(AppQrEntry)
//  로그인한 상점관리자의 상점 ≠ storeNo → "상점 불일치 / 다른 상점 아이디로 로그인하셨습니다" → 그 상점 로그인
//  같으면 검증(POST …/verify?storeNo=, 서버도 같은 불일치 검사) → 성공/실패 화면(주소에 storeNo)
//  미로그인(401)은 useAxiosAdminFetch 가 /mobile/login/{storeNo}?redirect= 로 보낸다(기프트와 같음).
//  이전 주소(/mobile/couponverifyconfirm/{번호}, 상점번호 없음)는 로그인 상점으로 새 주소에 다시 보낸다.
// verifyCoupon()은 실패도 200 + {result:false,failDesc}로 내려준다.
import { useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import LazySpinner from '../admin/LazySpinner';

export default function CouponVerifyConfirm() {
  const navigate = useNavigate();
  const { storeNo, promotionCouponNo } = useParams();
  const { fetchData } = useAxiosAdminFetch();

  const mismatch = useCallback(() => {
    Swal.fire({
      icon: 'warning',
      title: '상점 불일치',
      text: '다른 상점 아이디로 로그인하셨습니다.',
      confirmButtonText: '확인',
    }).then(() => {
      navigate(`/mobile/login/${storeNo}`);
    });
  }, [navigate, storeNo]);

  const verifyScan = useCallback(async () => {
    // 로그인한 상점관리자의 상점(GET profile). 401이면 fetchData 가 로그인 화면으로 보냄
    const profile = await fetchData({
      method: 'GET',
      url: `${process.env.REACT_APP_API_SERVER}profile`,
    });
    if (profile?.status === 401) return;
    const loginStoreNo = profile?.data?.data?.storeNo;

    // 이전 주소(상점번호 없음) → 로그인 상점으로 새 주소
    if (!storeNo) {
      if (loginStoreNo != null) {
        navigate(`/mobile/couponverifyconfirm/${loginStoreNo}/${promotionCouponNo}`, { replace: true });
      } else {
        navigate(`/mobile/login?redirect=${encodeURIComponent(`/mobile/couponverifyconfirm/${promotionCouponNo}`)}`, {
          replace: true,
        });
      }
      return;
    }

    // 기프트 검증과 같은 상점 불일치 처리(서버 응답 전에 먼저 — 이전 API 는 storeNo 를 안 봄)
    if (loginStoreNo != null && String(loginStoreNo) !== String(storeNo)) {
      mismatch();
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_SERVER}promotion/coupon/${promotionCouponNo}/verify?storeNo=${storeNo}`;
      const response = await fetchData({ method: 'POST', url, data: {} });
      if (response?.status === 401) return;
      if (response?.status === 403 || response?.data?.resultCode === 403) {
        mismatch();
        return;
      }

      const result = response?.data?.data;
      if (result?.result) {
        navigate(`/mobile/couponverifysuccess/${storeNo}/${promotionCouponNo}`, {
          replace: true,
          state: result,
        });
      } else {
        navigate(`/mobile/couponverifyfailure/${storeNo}/${promotionCouponNo}`, {
          replace: true,
          state: result ?? { failDesc: response?.data?.message },
        });
      }
    } catch (error) {
      navigate(`/mobile/couponverifyfailure/${storeNo}/${promotionCouponNo}`, {
        replace: true,
        state: { failDesc: error?.response?.data?.message },
      });
    }
  }, [storeNo, promotionCouponNo, fetchData, navigate, mismatch]);

  useEffect(() => {
    verifyScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LazySpinner />;
}
